import { emailOTPClient } from 'better-auth/client/plugins';
// Session handling — API §2.1, App Flow §1. Bearer tokens (Better Auth `bearer` plugin) so the
// PWA works cross-origin without third-party cookies; drafts survive a failed refresh (API §6).
import { createAuthClient } from 'better-auth/react';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';
const TOKEN_KEY = 'roots:token';
const USER_KEY = 'roots:user';

export const readToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

const writeToken = (token: string | null) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* private mode */
  }
};

/** Better Auth client error (better-fetch): `code` is set for Better Auth's own errors. */
export interface AuthClientError {
  code?: string;
  message?: string;
  status?: number;
}

/** The slice of the Better Auth client the app uses (its inferred type is not portable). */
interface RootsAuthClient {
  signOut: () => Promise<unknown>;
  signIn: {
    emailOtp: (args: { email: string; otp: string }) => Promise<{
      data?: { token?: string } | null;
      error?: AuthClientError | null;
    }>;
    social: (args: {
      provider: 'google';
      callbackURL: string;
      newUserCallbackURL: string;
      errorCallbackURL: string;
    }) => Promise<{ error?: AuthClientError | null }>;
  };
  emailOtp: {
    sendVerificationOtp: (args: { email: string; type: 'sign-in' }) => Promise<{
      error?: AuthClientError | null;
    }>;
  };
}

export const authClient: RootsAuthClient = createAuthClient({
  baseURL: API_URL,
  basePath: '/auth',
  plugins: [emailOTPClient()],
  fetchOptions: {
    credentials: 'include',
    auth: { type: 'Bearer', token: () => readToken() ?? '' },
    onSuccess: (ctx: { response: Response }) => {
      const token = ctx.response.headers.get('set-auth-token');
      if (token) writeToken(token);
    },
  },
}) as unknown as RootsAuthClient;

/** Sign-in responses also carry the session token in the body. */
export const storeSessionToken = (token: string | null | undefined) => {
  if (token) writeToken(token);
};

// Google sign-in ends on the API origin with a session cookie, not a bearer token. The
// cookie is first-party (roots.arnavs.page and api.roots.arnavs.page are same-site), so
// after the redirect we trade it for the token once. Scoped by a marker so a signed-out
// device never silently resumes a session.
const OAUTH_MARKER = 'roots:oauth-started';
const OAUTH_MARKER_TTL_MS = 15 * 60 * 1000;

export const markOAuthStarted = () => {
  try {
    localStorage.setItem(OAUTH_MARKER, String(Date.now()));
  } catch {
    /* private mode */
  }
};

function takeOAuthMarker(): boolean {
  try {
    const started = Number(localStorage.getItem(OAUTH_MARKER) ?? 0);
    localStorage.removeItem(OAUTH_MARKER);
    return started > 0 && Date.now() - started < OAUTH_MARKER_TTL_MS;
  } catch {
    return false;
  }
}

async function claimCookieSession() {
  const res = await fetch(`${API_URL}/auth/get-session`, { credentials: 'include' });
  if (!res.ok) return;
  const body = (await res.json()) as { session?: { token?: string } } | null;
  storeSessionToken(body?.session?.token);
}

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  avatar_url: string | null;
  birthday: string | null;
  city: string | null;
  text_size_pref: 'default' | 'large';
}

interface AuthState {
  user: SessionUser | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<SessionUser | null>;
  setUser: (user: SessionUser | null) => void;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  signOut: async () => undefined,
  refresh: async () => null,
  setUser: () => undefined,
});

function cachedUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as SessionUser) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<SessionUser | null>(cachedUser);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((next: SessionUser | null) => {
    setUserState(next);
    try {
      if (next) {
        localStorage.setItem(USER_KEY, JSON.stringify(next));
        localStorage.setItem('roots:text-size', next.text_size_pref);
        document.documentElement.dataset.textSize = next.text_size_pref;
      } else {
        localStorage.removeItem(USER_KEY);
      }
    } catch {
      /* private mode */
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      if (!readToken() && takeOAuthMarker()) await claimCookieSession();
      if (!readToken()) {
        setUser(null);
        return null;
      }
      const res = await fetch(`${API_URL}/v1/users/me`, {
        headers: { authorization: `Bearer ${readToken()}` },
      });
      if (!res.ok) {
        // Drafts stay in Dexie; only the session is dropped (API §6).
        if (res.status === 401) writeToken(null);
        setUser(null);
        return null;
      }
      const me = (await res.json()) as SessionUser;
      setUser(me);
      return me;
    } catch {
      return cachedUser(); // offline: trust the cached identity
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      // Stop this device receiving the account's pushes (lazy import: push.ts uses api.ts,
      // which imports this file).
      await (await import('./push.js')).detachPush();
    } catch {
      /* offline or already gone; the server prunes dead devices on the next send */
    }
    try {
      await authClient.signOut();
    } catch {
      /* offline sign-out still clears locally */
    }
    writeToken(null);
    setUser(null);
  }, [setUser]);

  const value = useMemo(
    () => ({ user, loading, signOut, refresh, setUser }),
    [user, loading, signOut, refresh, setUser],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export { API_URL };
